'use client';

import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { ButtonV2 } from '@/components/ui/ButtonV2';
import {
  getClientContextReadiness,
  type ClientContextReadiness,
  type ClientReadinessDimension,
} from '@/lib/clientContextReadiness';
import { cn, getInitials } from '@/lib/utils';
import type { Client } from '@/types';

type ClientContextSwitcherProps = {
  clients: Client[];
  activeClient: Client | null;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onClientSelect: (clientId: string | null) => void;
  onCreateNew?: () => void;
  buttonId?: string;
  label?: string;
  align?: 'left' | 'right';
  compact?: boolean;
  presentation?: 'dropdown' | 'sheet';
  className?: string;
};

function readinessTone(readiness: ClientContextReadiness): string {
  if (readiness.level === 'ready') return 'text-success border-success/30 bg-success/10';
  if (readiness.level === 'partial') return 'text-warning border-warning/30 bg-warning/10';
  if (readiness.level === 'general') return 'text-muted-foreground border-border/60 bg-muted/20';
  return 'text-warning border-warning/40 bg-warning/10';
}

function dimensionTone(status: ClientReadinessDimension['status']): string {
  if (status === 'ready') return 'border-success/25 bg-success/10 text-success';
  if (status === 'weak') return 'border-warning/25 bg-warning/10 text-warning';
  return 'border-border/50 bg-muted/20 text-muted-foreground';
}

function DimensionPill({ dimension }: { dimension: ClientReadinessDimension }) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-1 text-[11px]',
        dimensionTone(dimension.status),
      )}
      title={dimension.detail}
    >
      <span className="truncate">{dimension.label}</span>
      <span className="font-semibold tabular-nums">{dimension.count}</span>
    </span>
  );
}

function ReadinessSummary({ readiness }: { readiness: ClientContextReadiness }) {
  return (
    <div className="rounded-xl border border-border/50 bg-background/50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">
            {readiness.clientName || 'General mode'}
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {readiness.industry || readiness.summary}
          </p>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full border px-2 py-1 text-[11px] font-semibold',
            readinessTone(readiness),
          )}
        >
          {readiness.summary}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {readiness.dimensions.map((dimension) => (
          <DimensionPill key={dimension.key} dimension={dimension} />
        ))}
      </div>
      {readiness.warnings.length > 0 && (
        <div className="mt-3 space-y-1">
          {readiness.warnings.slice(0, 2).map((warning) => (
            <div key={warning} className="flex gap-1.5 text-xs text-warning">
              <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{warning}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function readinessGroupLabel(level: ClientContextReadiness['level']): string {
  if (level === 'ready') return 'Ready context';
  if (level === 'partial') return 'Partial context';
  if (level === 'general') return 'General mode';
  return 'Weak context';
}

export function ClientContextSwitcher({
  clients,
  activeClient,
  isOpen,
  onToggle,
  onClose,
  onClientSelect,
  onCreateNew,
  buttonId,
  label = 'Client context',
  align = 'left',
  compact = false,
  presentation = 'dropdown',
  className,
}: ClientContextSwitcherProps) {
  const [query, setQuery] = useState('');
  const listboxId = buttonId ? `${buttonId}-listbox` : undefined;
  const activeReadiness = useMemo(
    () => getClientContextReadiness(activeClient),
    [activeClient],
  );
  const filteredClients = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return clients;
    return clients.filter((client) => {
      const haystack = [
        client.name,
        client.shortName,
        client.industry,
        ...(client.aliases || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(normalized);
    });
  }, [clients, query]);
  const groupedClients = useMemo(() => {
    const groups: Array<{
      key: ClientContextReadiness['level'];
      label: string;
      clients: Client[];
    }> = [
      { key: 'ready', label: readinessGroupLabel('ready'), clients: [] },
      { key: 'partial', label: readinessGroupLabel('partial'), clients: [] },
      { key: 'weak', label: readinessGroupLabel('weak'), clients: [] },
    ];

    for (const client of filteredClients) {
      const readiness = getClientContextReadiness(client);
      const target = groups.find((group) => group.key === readiness.level) || groups[2];
      target.clients.push(client);
    }

    return groups.filter((group) => group.clients.length > 0);
  }, [filteredClients]);

  const panelContent = (
    <>
      <div className="border-b border-border/70 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{label}</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Search clients or switch to general mode.
            </p>
          </div>
          <ButtonV2
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close client context switcher"
          >
            <XMarkIcon className="h-4 w-4" />
          </ButtonV2>
        </div>
        <div className="relative mt-3">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            aria-label="Search clients"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search clients..."
            className="h-10 w-full rounded-lg border border-border/60 bg-background/70 pl-9 pr-3 text-sm text-foreground outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15"
          />
        </div>
      </div>

      <div className="max-h-[70vh] overflow-y-auto p-3">
        <ReadinessSummary readiness={activeReadiness} />

        <div id={listboxId} role="listbox" aria-label={`${label} options`} className="mt-3 space-y-3">
          <button
            type="button"
            role="option"
            aria-selected={!activeClient}
            onClick={() => onClientSelect(null)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-muted/50 focus:bg-muted/70 focus:outline-none',
              !activeClient && 'bg-muted/40',
            )}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/30">
              <UserGroupIcon className="h-4 w-4 text-muted-foreground" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-foreground">General mode</span>
              <span className="block truncate text-xs text-muted-foreground">No client-specific context</span>
            </span>
            {!activeClient && <CheckCircleIcon className="h-5 w-5 shrink-0 text-success" />}
          </button>

          {groupedClients.map((group) => (
            <div key={group.key} role="group" aria-label={group.label} className="space-y-1">
              <div className="flex items-center justify-between px-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {group.label}
                </p>
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  {group.clients.length}
                </span>
              </div>
              {group.clients.map((client) => {
                const readiness = getClientContextReadiness(client);
                const isActive = activeClient?.id === client.id;
                return (
                  <button
                    key={client.id}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => onClientSelect(client.id)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-muted/50 focus:bg-muted/70 focus:outline-none',
                      isActive && 'bg-primary/10',
                    )}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
                      {getInitials(client.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-foreground">{client.name}</span>
                        {readiness.level === 'ready' && <ShieldCheckIcon className="h-3.5 w-3.5 shrink-0 text-success" />}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {client.industry || 'No industry'} · {readiness.summary}
                      </span>
                      {readiness.warnings[0] && (
                        <span className="mt-1 block truncate text-[11px] text-warning">
                          {readiness.warnings[0]}
                        </span>
                      )}
                    </span>
                    {isActive && <CheckCircleIcon className="mt-1 h-5 w-5 shrink-0 text-success" />}
                  </button>
                );
              })}
            </div>
          ))}

          {filteredClients.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              No clients match &quot;{query}&quot;.
            </p>
          )}
        </div>

        {onCreateNew && (
          <div className="mt-3 border-t border-border/60 pt-3">
            <button
              type="button"
              onClick={onCreateNew}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-primary transition-colors hover:bg-primary/10 focus:bg-primary/10 focus:outline-none"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <PlusCircleIcon className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-sm font-semibold">Create new client</span>
                <span className="block text-xs">Add context before running account-specific work</span>
              </span>
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className={cn('relative z-50', className)}>
      <motion.button
        id={buttonId}
        type="button"
        onClick={onToggle}
        className={cn(
          'flex min-h-10 w-full items-center gap-2 rounded-xl border px-3 py-2 text-left font-medium transition-all duration-200',
          'bg-background/70 backdrop-blur-xl hover:bg-background/90 focus:outline-none focus:ring-2 focus:ring-primary/40',
          activeClient
            ? 'border-success/30 text-foreground ring-1 ring-success/20'
            : 'border-warning/40 text-foreground ring-1 ring-warning/20',
          compact ? 'max-w-[240px]' : 'max-w-[320px]',
        )}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
      >
        {activeClient ? (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success/15 text-sm font-bold text-success">
            {getInitials(activeClient.name)}
          </span>
        ) : (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-warning/15">
            <UserGroupIcon className="h-4 w-4 text-warning" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">
            {activeClient?.name || 'General mode'}
          </span>
          {!compact && (
            <span className="block truncate text-xs text-muted-foreground">
              {activeClient?.industry || label}
            </span>
          )}
        </span>
        <span
          className={cn(
            'hidden rounded-full border px-2 py-0.5 text-[10px] font-semibold',
            !compact && 'sm:inline-flex',
            readinessTone(activeReadiness),
          )}
        >
          {activeReadiness.level === 'ready'
            ? 'Ready'
            : activeReadiness.level === 'general'
              ? 'General'
              : activeReadiness.level === 'partial'
                ? 'Partial'
                : 'Weak'}
        </span>
        <ChevronDownIcon className={cn('h-4 w-4 shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </motion.button>

      <AnimatePresence>
        {isOpen && presentation === 'sheet' && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end bg-black/35 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
            onClick={onClose}
          >
            <motion.div
              className="max-h-[86vh] w-full overflow-hidden rounded-t-2xl border border-border bg-card shadow-2xl"
              initial={{ y: 32 }}
              animate={{ y: 0 }}
              exit={{ y: 32 }}
              transition={{ duration: 0.18 }}
              onClick={(event) => event.stopPropagation()}
            >
              {panelContent}
            </motion.div>
          </motion.div>
        )}

        {isOpen && presentation === 'dropdown' && (
          <motion.div
            className={cn(
              'absolute top-full z-50 mt-2 w-[min(92vw,420px)] overflow-hidden rounded-xl border border-border bg-card shadow-2xl',
              align === 'right' ? 'right-0' : 'left-0',
            )}
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.16 }}
          >
            {panelContent}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ClientContextSwitcher;
